import React, { useEffect } from "react";
import { UseFormReturnType, useForm } from "@mantine/form";
import { UseMutationResult } from "react-query";
import { Box, Flex, Input, Text, VStack } from "@chakra-ui/react";
import { transformObjectToArray } from "../../helpers/functions/transformObjectToArray";

interface DefaultPricingProps {
  form: UseFormReturnType<any, (values: any) => any>;
  mutation: UseMutationResult<any, any, any, unknown>;
}

export const DefaultPricing = ({ form, mutation }: DefaultPricingProps) => {
  const priceForm = useForm({
    initialValues: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
      xl: '',
      _2xl: ''
    },

    validate: {
      xs: (value) => (
        !value ? 'XS field is required' : null
      ),
      sm: (value) => (
        !value ? 'SM field is required' : null
      ),
      md: (value) => (
        !value ? 'MD field is required' : null
      ),
      lg: (value) => (
        !value ? 'LG field is required' : null
      ),
      xl: (value) => (
        !value ? 'XL field is required' : null
      ),
      _2xl: (value) => (
        !value ? '2XL field is required' : null
      ),
    },
  });

  useEffect(() => {
    priceForm.validate()

    if (priceForm.isValid()) {
      form.setValues({
        ...form.values,
        price: transformObjectToArray(priceForm.values)
      })
    } else {
      form.setValues({
        ...form.values,
        price: []
      })
    }
  }, [priceForm.values])

  return (
    <VStack spacing='5'>
      <Flex w='100%' justifyContent='space-between' gap={5} alignItems='start'>
        <Box w='100%'>
          <Text fontWeight='semibold' color='gray.500' fontSize='sm'>
            XS
          </Text>

          <Box mt='1'>
            <Input
              type="number"
              {...priceForm.getInputProps('xs')}
              disabled={mutation.isLoading}
              size='md' mt={1} placeholder="xs: 4000"
              border={priceForm.errors.xs ? '2px' : '1px'}
              borderColor={priceForm.errors.xs ? 'red.500' : 'gray.500'} color='gray.500'
              focusBorderColor={priceForm.errors.xs ? 'red.500' : 'gray.500'}
              _hover={{ outlineColor: 'transparent' }}
            />

            {priceForm.errors.xs &&
              <Text fontWeight='semibold' mt={1} color='red.500' fontSize='sm'>
                {priceForm.errors.xs}
              </Text>
            }
          </Box>
        </Box>

        <Box w='100%'>
          <Text fontWeight='semibold' color='gray.500' fontSize='sm'>
            SM
          </Text>

          <Box mt='1'>
            <Input
              type="number"
              {...priceForm.getInputProps('sm')}
              disabled={mutation.isLoading}
              size='md' mt={1} placeholder="sm: 4000"
              border={priceForm.errors.sm ? '2px' : '1px'}
              borderColor={priceForm.errors.sm ? 'red.500' : 'gray.500'} color='gray.500'
              focusBorderColor={priceForm.errors.sm ? 'red.500' : 'gray.500'}
              _hover={{ outlineColor: 'transparent' }}
            />

            {priceForm.errors.sm &&
              <Text fontWeight='semibold' mt={1} color='red.500' fontSize='sm'>
                {priceForm.errors.sm}
              </Text>
            }
          </Box>
        </Box>
      </Flex>

      <Flex w='100%' justifyContent='space-between' gap={5} alignItems='start'>
        <Box w='100%'>
          <Text fontWeight='semibold' color='gray.500' fontSize='sm'>
            MD
          </Text>

          <Box mt='1'>
            <Input
              type="number"
              {...priceForm.getInputProps('md')}
              disabled={mutation.isLoading}
              size='md' mt={1} placeholder="md: 4000"
              border={priceForm.errors.md ? '2px' : '1px'}
              borderColor={priceForm.errors.md ? 'red.500' : 'gray.500'} color='gray.500'
              focusBorderColor={priceForm.errors.md ? 'red.500' : 'gray.500'}
              _hover={{ outlineColor: 'transparent' }}
            />

            {priceForm.errors.md &&
              <Text fontWeight='semibold' mt={1} color='red.500' fontSize='sm'>
                {priceForm.errors.md}
              </Text>
            }
          </Box>
        </Box>

        <Box w='100%'>
          <Text fontWeight='semibold' color='gray.500' fontSize='sm'>
            LG
          </Text>

          <Box mt='1'>
            <Input
              type="number"
              {...priceForm.getInputProps('lg')}
              disabled={mutation.isLoading}
              size='md' mt={1} placeholder="lg: 4000"
              border={priceForm.errors.lg ? '2px' : '1px'}
              borderColor={priceForm.errors.lg ? 'red.500' : 'gray.500'} color='gray.500'
              focusBorderColor={priceForm.errors.lg ? 'red.500' : 'gray.500'}
              _hover={{ outlineColor: 'transparent' }}
            />

            {priceForm.errors.lg &&
              <Text fontWeight='semibold' mt={1} color='red.500' fontSize='sm'>
                {priceForm.errors.lg}
              </Text>
            }
          </Box>
        </Box>
      </Flex>

      <Flex w='100%' justifyContent='space-between' gap={5} alignItems='start'>
        <Box w='100%'>
          <Text fontWeight='semibold' color='gray.500' fontSize='sm'>
            XL
          </Text>

          <Box mt='1'>
            <Input
              type="number"
              {...priceForm.getInputProps('xl')}
              disabled={mutation.isLoading}
              size='md' mt={1} placeholder="xl: 4000"
              border={priceForm.errors.xl ? '2px' : '1px'}
              borderColor={priceForm.errors.xl ? 'red.500' : 'gray.500'} color='gray.500'
              focusBorderColor={priceForm.errors.xl ? 'red.500' : 'gray.500'}
              _hover={{ outlineColor: 'transparent' }}
            />

            {priceForm.errors.xl &&
              <Text fontWeight='semibold' mt={1} color='red.500' fontSize='sm'>
                {priceForm.errors.xl}
              </Text>
            }
          </Box>
        </Box>

        <Box w='100%'>
          <Text fontWeight='semibold' color='gray.500' fontSize='sm'>
            2xl
          </Text>

          <Box mt='1'>
            <Input
              type="number"
              {...priceForm.getInputProps('_2xl')}
              disabled={mutation.isLoading}
              size='md' mt={1} placeholder="2xl: 4000"
              border={priceForm.errors._2xl ? '2px' : '1px'}
              borderColor={priceForm.errors._2xl ? 'red.500' : 'gray.500'} color='gray.500'
              focusBorderColor={priceForm.errors._2xl ? 'red.500' : 'gray.500'}
              _hover={{ outlineColor: 'transparent' }}
            />

            {priceForm.errors._2xl &&
              <Text fontWeight='semibold' mt={1} color='red.500' fontSize='sm'>
                {priceForm.errors._2xl}
              </Text>
            }
          </Box>
        </Box>
      </Flex>
    </VStack>
  )
}